import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useAppDispatch, useAppSelector } from '@/lib/RTK/hooks';
import {
    setFormModal,
    setSelectedTurn,
} from '@/lib/RTK/features/AdmissionSlice';
import { TypeEnabaleBtn } from './types/AdmissionType';
import Image from 'next/image';
import LoginFunction from '../auth/LoginFunction';
import logo from '@/assets/logo.png';
import { CiCircleRemove } from 'react-icons/ci';
import { useRouter } from 'next/navigation';
import { formatNameDoctor } from '@/constant/scripts';
import { GiBackwardTime } from 'react-icons/gi';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';

const FormLoginModal = (props: TypeEnabaleBtn) => {
    const router = useRouter();
    const [showTurnAlert, setShowTurnAlert] = useState(false);
    const [redirectPath, setRedirectPath] = useState<string | null>(null);

    const {
        enableSubmit,
        handleResetPosition,
        textBtn,
        pathNameRoute,
        nameLabRadio,
        buttonElement,
        defaultOpen,
        docname,
        docid,
        isTurn,
        endFunction,
    } = props;

    const {
        postionLogin_numberor_very,
        mobile,
        token,
        register,
        tracking_code,
    } = useAppSelector((state) => state.auth_token_slice);

    const dispatch = useAppDispatch();
    const updatedClinicId = useAppSelector(
        (state) => state.admission.updateClinicId,
    );

    useEffect(() => {
        if (!token && !mobile) return;

        if (register === '1') {
            dispatch(setFormModal('formturn'));
            return;
        }

        if (!isTurn || !tracking_code) return;

        if (tracking_code !== 'noTracking') {
            setShowTurnAlert(true);
        } else {
            setRedirectPath(
                `/doctor/${docid}/${formatNameDoctor(
                    docname,
                )}/${updatedClinicId}/turn`,
            );
        }
    }, [
        token,
        mobile,
        register,
        isTurn,
        tracking_code,
        docid,
        docname,
        updatedClinicId,
        dispatch,
    ]);

    useEffect(() => {
        if (redirectPath) {
            router.push(redirectPath);
            setRedirectPath(null);
        }
    }, [redirectPath, router]);

    const queryClient = useQueryClient();
    const prevTurn:
        | {
              time: string;
              doctor_name: string;
              date: string;
              gdate: string;
              office: string;
          }
        | undefined = queryClient.getQueryData([
        'check-reserve',
        mobile,
        token,
    ]);

    return (
        <Dialog.Root
            defaultOpen={defaultOpen ?? true}
            onOpenChange={(open) => {
                if (!open) handleResetPosition();
            }}
        >
            <Dialog.Trigger disabled={!enableSubmit} asChild>
                {buttonElement ? (
                    buttonElement
                ) : (
                    <Button color="green" variant="primaryShaded">
                        {textBtn ? 'درخواست نوبت' : 'فرم ثبت نام'}
                    </Button>
                )}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-[#0000002b] z-50 opacity-100" />
                <Dialog.Content
                    className={`${
                        showTurnAlert
                            ? '!py-7'
                            : 'pb-9 pt-[30px] md:!pt-[40px] md:!pb-[50px]'
                    } flex_col justify-center items-center gap-3 !rounded-3xl data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] z-[50] translate-y-[-50%] bg-white px-5 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none`}
                >
                    <Dialog.Title className="sr-only"> </Dialog.Title>
                    {showTurnAlert && prevTurn ? (
                        <div className="text-center w-full">
                            <div className="bg-blue-shaded p-2 w-max mx-auto rounded-2xl flex justify-center items-center">
                                <GiBackwardTime className="text-blue text-2xl" />
                            </div>
                            <p className="font-semibold pt-4">
                                یک نوبت برای دکتر {prevTurn?.doctor_name} در
                                تاریخ {prevTurn?.date} ، <br /> ساعت{' '}
                                {prevTurn?.time + ' '} از قبل برای شما رزرو شده
                                است.
                            </p>
                            <p className="pt-1">
                                آیا مایل به ثبت نوبت جدید هستید ؟
                            </p>

                            <div className="flex gap-4 mt-6 justify-end items-center ">
                                <Button
                                    variant={'primaryShaded'}
                                    className="!py-2 !px-6 bg-blue-shaded hover:!bg-blue-shaded text-blue !rounded-md w-full"
                                    onClick={() => {
                                        dispatch(setFormModal(''));
                                        dispatch(
                                            setSelectedTurn({
                                                date: prevTurn?.gdate || '',
                                                time: prevTurn?.time || '',
                                                jalali: prevTurn?.date || '',
                                                childId: '',
                                            }),
                                        );
                                        router.push(
                                            `/doctor/${docid}/${formatNameDoctor(
                                                docname,
                                            )}/${
                                                prevTurn.office
                                            }/continue/checkout`,
                                        );
                                    }}
                                >
                                    تکمیل نوبت قبل
                                </Button>
                                <Button
                                    className="!py-2 !px-6 !rounded-md w-full bg-blue !text-white"
                                    onClick={() => {
                                        dispatch(setFormModal(''));
                                        router.push(
                                            `/doctor/${docid}/${formatNameDoctor(
                                                docname,
                                            )}/${updatedClinicId}/turn`,
                                        );
                                    }}
                                >
                                    ثبت نوبت جدید
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full md:!px-4 justify-between flex flex-col items-center">
                            <Image
                                height={52}
                                width={200}
                                src={logo}
                                alt="درمان شیرین"
                                className="bg-cover self-center"
                            />
                            <div className="w-full pt-5 md:!pt-0">
                                <p className="font-semibold pb-2">
                                    {postionLogin_numberor_very === 'number'
                                        ? 'ورود به حساب کاربری'
                                        : 'دریافت کد تایید'}
                                </p>
                                {postionLogin_numberor_very === 'number' ? (
                                    <p className="text-sm text-[#222222A3] font-medium">
                                        برای استفاده از خدمات درمان شیرین شماره
                                        موبایل خود را وارد کنید.
                                    </p>
                                ) : (
                                    ''
                                )}
                            </div>

                            <LoginFunction
                                endFunction={endFunction}
                                nameLabRadio={nameLabRadio}
                                pathNameRoute={pathNameRoute}
                            />
                        </div>
                    )}

                    <Dialog.Close asChild>
                        <button
                            className="font-bold text-2xl hover:bg-violet4 focus:shadow-violet7 absolute top-[10px] left-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                            aria-label="Close"
                        >
                            <CiCircleRemove />
                        </button>
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default FormLoginModal;
